import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Loader } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import useConfirm from 'hooks/use-confirm';
import useLocationApi from 'hooks/use-location-api';
import { Location } from 'types';
import { formatLocation } from 'utils/format';

const ViewLocation: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { getLocation, deleteLocation } = useLocationApi();
  const { getConfirmation, ConfirmationDialog } = useConfirm();

  const [loading, setLoading] = useState<boolean>(false);
  const [location, setLocation] = useState<Location | null>();

  const loadLocation = useCallback(() => {
    setLoading(true);
    getLocation(Number(params.id))
      .then(setLocation)
      .finally(() => setLoading(false));
  }, [params.id, getLocation, setLocation]);

  useEffect(loadLocation, [loadLocation]);

  const toggleDeleted = useCallback(async () => {
    if (!location) return;
    const confirm = await getConfirmation(
      `${!location.deleted ? 'Deactivate' : 'Reactivate'} Location?`,
      `Location ${formatLocation(location)}`
    );
    if (confirm) {
      const response = await deleteLocation(location.id, !location.deleted);
      if (response) loadLocation();
    }
  }, [location, deleteLocation, getConfirmation, navigate]);

  return (
    <Loader loading={loading}>
      <div className="page" id="customer-details">
        <div className="page-header">
          <h1>{formatLocation(location) || 'N/A'}</h1>
          {!!location?.deleted && <label id="location-deleted">DEACTIVATED</label>}
          <div className="d-flex g-3 flex-row-reverse">
            <button
              className="btn-primary text-uppercase"
              onClick={() => navigate('edit', { replace: true })}
              data-cy="location-edit-button"
            >
              Edit
            </button>
            {!location?.deleted ? (
              <button
                className="btn-danger text-uppercase"
                onClick={toggleDeleted}
                data-cy="location-deactivate-button"
              >
                Deactivate
              </button>
            ) : (
              <button className="btn-outline text-uppercase" onClick={toggleDeleted} data-cy="location-activate-button">
                Activate
              </button>
            )}
            <button className="btn-back" onClick={() => navigate('/settings/locations', { replace: true })}>
              <span className="material-icons">arrow_back_ios</span>
              BACK
            </button>
          </div>
        </div>
        <div className="card-primary">
          <div className="card-header px-3">
            <h4 className="text-uppercase">Location Information</h4>
          </div>
          <div className="card-body">
            <dl className="grid-3 p-2" style={{ maxWidth: 1200 }}>
              <div>
                <dt className="text-info-main">NAME</dt>
                <dd className="text-info-dark">{location?.name ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">CODE</dt>
                <dd className="text-info-dark">{location?.code ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">TIMEZONE</dt>
                <dd className="text-info-dark">{location?.timeZone ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">ADDRESS</dt>
                <dd className="text-info-dark">{location?.address ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">ADDRESS 2</dt>
                <dd className="text-info-dark">{location?.address2 ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">ZIP CODE</dt>
                <dd className="text-info-dark">{location?.zipCode ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">CITY</dt>
                <dd className="text-info-dark">{location?.city ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">STATE</dt>
                <dd className="text-info-dark">{location?.state ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">COUNTRY</dt>
                <dd className="text-info-dark">{location?.country ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">REMIT TO</dt>
                <dd className="text-info-dark">{location?.remitTo ?? ''}</dd>
              </div>
              <div>
                <dt className="text-info-main">INVOICE REMIT TO</dt>
                <dd className="text-info-dark">{!location?.remitTo ? 'NO' : 'YES'}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
      <ConfirmationDialog danger />
      <ErrorDialog />
      <SuccessDialog />
    </Loader>
  );
};

export default ViewLocation;
